import React, { useState } from 'react';

import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { reportError } from '@modernloop/shared/observability';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import { OrganizationSettingsTab, getOrganizationSettingUrls } from 'src/urls/getOrganizationSettingUrls';

import useCheckCreateCandidatePermission from './useCheckCreateCandidatePermission';

enum PermissionCheckStatus {
  Pending = 'Pending',
  Success = 'Success',
  Failed = 'Failed',
}

type Props = {
  jobId: string;
  onHasPermission: () => void;
  onClose: () => void;
};

const LeverCreateCandidatePermissionDialog = ({ jobId, onHasPermission, onClose }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [permissionCheckStatus, setPermissionCheckStatus] = useState(PermissionCheckStatus.Pending);
  const [testCandidateAtsUrl, setTestCandidateAtsUrl] = useState<string | undefined>();

  const { enqueueSnackbar } = useSnackbar();
  const checkCreateCandidatePermission = useCheckCreateCandidatePermission(jobId);

  const handleCheckPermission = async () => {
    try {
      setLoading(true);
      const { hasPermission: permission } = await checkCreateCandidatePermission();
      setPermissionCheckStatus(permission ? PermissionCheckStatus.Success : PermissionCheckStatus.Failed);
      setTestCandidateAtsUrl(testCandidateAtsUrl);
    } catch (error) {
      enqueueSnackbar(t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.failedToCheckPermissions'), {
        variant: 'error',
      });
      reportError(error, { jobId });
    } finally {
      setLoading(false);
    }
  };

  const permissionsJsx = (
    <List sx={{ listStyleType: 'disc', margin: (theme) => theme.spacing(0, 2), padding: 0 }}>
      <ListItem sx={{ display: 'list-item' }}>contact:write:admin</ListItem>
      <ListItem sx={{ display: 'list-item' }}>opportunities:write:admin</ListItem>
      <ListItem sx={{ display: 'list-item' }}>sources:read:admin</ListItem>
    </List>
  );

  return (
    <Dialog
      open
      maxWidth="xs"
      onClose={onClose}
      title={t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.lever.title')}
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src="/static/images/scheduling/warning_64.png"
          alt={t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.changingCalendarWarningAlt')}
        />
      }
      cancelOptions={{
        label: t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.cancel'),
        onClick: onClose,
      }}
      submitOptions={{
        label: t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.continue'),
        onClick: onHasPermission,
        isDisabled: permissionCheckStatus !== PermissionCheckStatus.Success,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle1">{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.step1')}</Typography>
        <Typography variant="body1">
          {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.lever.reAuthenticate')}
        </Typography>

        <Alert severity="warning">
          <List sx={{ listStyleType: 'disc', margin: (theme) => theme.spacing(0, 2), padding: 0 }}>
            <ListItem sx={{ display: 'list-item' }}>opportunities:write:admin</ListItem>
            <ListItem sx={{ display: 'list-item' }}>sources:read:admin</ListItem>
          </List>
        </Alert>

        <Box>
          <Button
            variant="contained"
            color="primary"
            href={getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATIONS })}
            target="_blank"
          >
            {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.lever.goToIntegrations')}
          </Button>
        </Box>

        <Typography variant="body2">
          {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.lever.notAdmin')}
        </Typography>

        <Typography variant="body2">
          <Trans
            i18nKey="hiring-dashboard-jobs:jobs.sourcingLinks.permissions.needHelp"
            components={{
              learnMoreLink: <Link href={HelpCenterUrl.SOURCING_LINK_ATS_INTEGRATION} target="_blank" />,
            }}
          />
        </Typography>

        <Divider flexItem />

        <Typography variant="subtitle1">{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.step2')}</Typography>
        <Typography variant="body1">{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.checkSetup')}</Typography>

        {permissionCheckStatus !== PermissionCheckStatus.Success && (
          <Box>
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={24} /> : undefined}
              onClick={handleCheckPermission}
            >
              {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.checkPermissions')}
            </Button>
          </Box>
        )}

        {permissionCheckStatus === PermissionCheckStatus.Success && (
          <Alert>
            <AlertTitle>{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.success')}</AlertTitle>
            {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.permissionsCorrect')}
          </Alert>
        )}

        {permissionCheckStatus === PermissionCheckStatus.Failed && (
          <Alert severity="error">
            {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.couldNotFindPermissions')} {permissionsJsx}
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
};

export default LeverCreateCandidatePermissionDialog;
